/**
 * Full-app welcome project mockup for Figma export.
 *
 * Uses real production components with their exact CSS tokens:
 *   - SpaceSwitcher  — store-free
 *   - AgentDock      — useAuth with "glass" fallback
 *   - CanvasLeftRail — zoomLevel prop bypasses store; topControls = DesktopRailUtilities mock
 */

import { useEffect, type CSSProperties } from "react";
import { cn } from "@/lib/cn";
import { Icons } from "@/components/ui/icons";
import {
  surfaceHoverClassName,
  surfaceRoleAttributes,
  surfaceRoleClassName,
} from "@/components/ui/surface-contract";
import { SpaceSwitcher } from "@/components/panels/space-switcher";
import type { SwitcherProject } from "@/components/panels/space-switcher";
import { AgentDock } from "@/components/canvas/agent-dock";
import { CanvasLeftRail } from "@/components/canvas/canvas-left-rail";
import type { ExportGroup } from "./export-types";
import {
  TaskboardShell,
  AgentListShell,
  ChecklistShell,
  WelcomeMarkdownShell,
  VideoShell,
} from "./export-app-frames";

/* ── Layout constants ─────────────────────────────────────────────────── */
const SCALE = 0.58;
const CANVAS_OFFSET_X = 80;
const CANVAS_OFFSET_Y = 60;
const RIGHT_PANEL_W = 236;
const LEFT_RAIL_W = 48;

/* ── Mock data ────────────────────────────────────────────────────────── */
const WELCOME_PROJECTS: SwitcherProject[] = [
  {
    id: "p1",
    name: "Welcome",
    color: "#30D158",
    active: true,
    spaces: [
      { id: "s1", label: "Main", active: true },
      { id: "s2", label: "Planning" },
    ],
  },
];

const DOCK_AGENTS = [
  { id: "a1", letter: "D", avatarSeed: "denker", color: "#30D158" },
  { id: "a2", letter: "R", avatarSeed: "researcher", color: "#60A5FA" },
  { id: "a3", letter: "M", avatarSeed: "marketer", color: "#F472B6" },
  { id: "a4", letter: "Ds", avatarSeed: "designer", color: "#A78BFA" },
];

/* ── Static rail top controls (mirrors DesktopRailUtilities) ─────────── */

const RAIL_BTN =
  "group relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-transparent bg-transparent text-muted transition-colors hover:text-primary";

const STATIC_TICKS = [
  { label: "Welcome to Denker", color: "var(--color-frame-markdown)" },
  { label: "How Denker works", color: "var(--color-frame-html)" },
  { label: "Start here", color: "rgb(113 113 122)" },
  { label: "Your Team", color: "#30D158" },
];

function RailTopControls() {
  return (
    <div className="flex flex-col items-center gap-1 pb-3">
      <button type="button" className={RAIL_BTN} aria-label="Search">
        <Icons.Search className="h-4 w-4" />
      </button>
      <button type="button" className={RAIL_BTN} aria-label="Notifications">
        <Icons.Bell className="h-4 w-4" />
      </button>
      <button type="button" className={RAIL_BTN} aria-label="Theme">
        <Icons.Moon className="h-4 w-4" />
      </button>
      <button type="button" className={RAIL_BTN} aria-label="User menu">
        <span className="text-[13px] font-semibold">J</span>
      </button>
      <div className="my-1 h-px w-6 bg-glass-stroke-faint" />
      {/* Static ruler ticks for the 4 non-taskboard seed frames — same
          denker-ruler-tick markup as FrameRulerStrip so the theme-adaptive
          readability rules (color-mix + glow) cover them. */}
      {STATIC_TICKS.map((t) => (
        <div
          key={t.label}
          className="flex h-[14px] w-full items-center justify-center"
        >
          <div
            className="denker-ruler-tick rounded-control-sm opacity-40"
            style={
              {
                "--denker-ruler-tick-color": t.color,
                width: 12,
                height: 2,
              } as CSSProperties
            }
          />
        </div>
      ))}
    </div>
  );
}

/* ── Canvas ───────────────────────────────────────────────────────────── */

function FrameAt({
  x,
  y,
  children,
}: {
  x: number;
  y: number;
  children: React.ReactNode;
}) {
  return (
    <div style={{ position: "absolute", left: x, top: y }}>{children}</div>
  );
}

function WelcomeCanvas() {
  return (
    <div
      className="absolute inset-0 overflow-hidden"
      style={{
        backgroundImage:
          "radial-gradient(circle, rgba(255,255,255,0.09) 1px, transparent 1px)",
        backgroundSize: "24px 24px",
      }}
    >
      <div
        style={{
          position: "absolute",
          left: CANVAS_OFFSET_X,
          top: CANVAS_OFFSET_Y,
          width: 1840,
          height: 980,
          transform: `scale(${SCALE})`,
          transformOrigin: "top left",
        }}
      >
        {/* taskboard: x=0   y=0   w=840 h=640 */}
        <FrameAt x={0} y={0}>
          <TaskboardShell />
        </FrameAt>
        {/* agents:    x=0   y=680 w=400 h=300 */}
        <FrameAt x={0} y={680}>
          <AgentListShell />
        </FrameAt>
        {/* checklist: x=440 y=680 w=400 h=300 */}
        <FrameAt x={440} y={680}>
          <ChecklistShell />
        </FrameAt>
        {/* welcome:   x=1000 y=0  w=400 h=300 */}
        <FrameAt x={1000} y={0}>
          <WelcomeMarkdownShell />
        </FrameAt>
        {/* video:     x=1440 y=0  w=400 h=300 */}
        <FrameAt x={1440} y={0}>
          <VideoShell />
        </FrameAt>
      </div>
    </div>
  );
}

/* ── Floating header ──────────────────────────────────────────────────── */

function FloatingHeader() {
  return (
    <div className="pointer-events-none absolute inset-x-0 top-2 z-20 flex justify-center">
      <div className="pointer-events-auto flex items-center gap-2">
        <SpaceSwitcher
          projects={WELCOME_PROJECTS}
          onSelectProject={() => {}}
          onSelectSpace={() => {}}
          onAddSpace={() => {}}
        />
        {/* Session indicator — 1 active session. Carries the same toolbar
            surface role as the production SessionsBubble so the number is
            covered by the theme-adaptive readable-overlay rules. */}
        <button
          type="button"
          className={cn(
            surfaceRoleClassName("toolbar"),
            surfaceHoverClassName(),
            "flex h-9 w-9 min-w-9 items-center justify-center border border-transparent px-0",
            "text-appkit-small font-semibold transition-transform hover:scale-105",
            "border-accent/40 bg-accent/18 text-primary",
          )}
          aria-label="1 active session"
          {...surfaceRoleAttributes("toolbar", {
            nativeLevel: "island",
            nativeGroup: "agent-dock",
          })}
        >
          <span className="tabular-nums">1</span>
        </button>
        {/* Agent avatar dock */}
        <AgentDock agents={DOCK_AGENTS} />
      </div>
    </div>
  );
}

/* ── Right history panel ──────────────────────────────────────────────── */

const HISTORY = [
  {
    time: "09:22 AM",
    who: "You",
    text: "Can you show me where is a coffee mug on my screenshot",
  },
  {
    time: "09:23 AM",
    who: "Denker",
    text: "I'm checking the screenshot — I'll point to the coffee mug directly.",
  },
  {
    time: "09:26 AM",
    who: "You",
    text: "Hey Denker can you look at my input bar",
  },
  {
    time: "09:27 AM",
    who: "Denker",
    text: "I can see the input bar — it's the bottom pill with the camera icon.",
  },
];

function RightHistoryPanel() {
  return (
    <div
      className={cn(
        "denker-glass-surface",
        "absolute z-10 flex flex-col overflow-hidden rounded-xl border border-glass-stroke",
      )}
      style={{ right: 12, top: 52, bottom: 80, width: RIGHT_PANEL_W }}
    >
      <div className="flex shrink-0 gap-0.5 border-b border-glass-stroke-faint px-1 pt-1">
        {["Tasks", "Details", "History"].map((tab) => (
          <button
            key={tab}
            type="button"
            className={cn(
              "flex-1 rounded-md py-1.5 text-[11px] font-medium transition-colors",
              tab === "History" ? "bg-white/[0.07] text-primary" : "text-muted",
            )}
          >
            {tab}
          </button>
        ))}
      </div>
      <div className="flex flex-1 flex-col overflow-y-auto p-1.5">
        {HISTORY.map((e, i) => (
          <div key={i} className="flex flex-col gap-0.5 rounded-lg px-2 py-1.5">
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] text-muted/50">{e.time}</span>
              <span
                className={cn(
                  "text-[10px] font-semibold",
                  e.who === "You" ? "text-secondary" : "text-accent",
                )}
              >
                {e.who}
              </span>
            </div>
            <p className="text-[10px] leading-snug text-secondary/75">
              {e.text}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Input bar shell ──────────────────────────────────────────────────── */

function InputBarShellMock() {
  return (
    <div
      className="absolute bottom-3 z-20"
      style={{ left: LEFT_RAIL_W + 8, right: RIGHT_PANEL_W + 20 }}
    >
      <div className="mx-auto max-w-[600px]">
        <div
          className={cn(
            "denker-glass-chrome",
            "flex min-h-[44px] items-center gap-0 rounded-full",
            "border border-glass-stroke bg-glass-fill px-1.5 py-1 shadow-glass backdrop-blur-glass",
          )}
        >
          {/* Agent stack — LEFT of the text area, matching real InputBarShell */}
          <AgentDock
            agents={DOCK_AGENTS.slice(0, 2)}
            className="border-0 bg-transparent p-0 shadow-none backdrop-filter-none"
          />
          {/* Divider */}
          <div className="mx-1.5 h-4 w-px shrink-0 bg-glass-stroke-subtle" />
          {/* Placeholder */}
          <span className="flex-1 px-0.5 text-[13px] text-muted/50">
            Ask Denker...
          </span>
          {/* Submit */}
          <button
            type="button"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent/90 text-white"
          >
            <Icons.ArrowUp className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Full-app component ───────────────────────────────────────────────── */

function WelcomeProjectApp() {
  useEffect(() => {
    const root = document.documentElement;
    const prev = {
      denk: root.dataset.desktopDenk,
      glass: root.dataset.desktopGlass,
      nativeGlass: root.dataset.desktopNativeGlass,
    };
    root.dataset.desktopDenk = "true";
    root.dataset.desktopGlass = "true";
    root.dataset.desktopNativeGlass = "true";
    return () => {
      if (prev.denk === undefined) delete root.dataset.desktopDenk;
      else root.dataset.desktopDenk = prev.denk;
      if (prev.glass === undefined) delete root.dataset.desktopGlass;
      else root.dataset.desktopGlass = prev.glass;
      if (prev.nativeGlass === undefined)
        delete root.dataset.desktopNativeGlass;
      else root.dataset.desktopNativeGlass = prev.nativeGlass;
    };
  }, []);

  return (
    <div
      className="relative overflow-hidden"
      style={{ width: 1440, height: 900 }}
    >
      {/* Atmospheric background */}
      <div
        className="absolute inset-0"
        style={{
          background: [
            "radial-gradient(ellipse 90% 50% at 50% -5%, rgba(245,220,180,0.11) 0%, transparent 60%)",
            "radial-gradient(ellipse 45% 70% at 12% 48%, rgba(195,175,145,0.07) 0%, transparent 52%)",
            "linear-gradient(180deg, #2A2118 0%, #18120D 55%, #201A12 100%)",
          ].join(", "),
        }}
      >
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              "repeating-linear-gradient(90deg, rgba(255,255,255,0.8) 0px, rgba(255,255,255,0.8) 1px, transparent 1px, transparent 34px)",
          }}
        />
      </div>

      <WelcomeCanvas />
      <FloatingHeader />

      {/* Left canvas rail with desktop utility controls at top + static ruler ticks */}
      <div
        className="absolute left-2 top-1/2 z-30 -translate-y-1/2"
        style={{ width: LEFT_RAIL_W }}
      >
        <CanvasLeftRail zoomLevel={100} topControls={<RailTopControls />} />
      </div>

      <RightHistoryPanel />
      <InputBarShellMock />
    </div>
  );
}

/* ── Export group ─────────────────────────────────────────────────────── */

export const APP_GROUP: ExportGroup = {
  category: "Full App",
  entries: [
    {
      id: "app-welcome-project",
      label: "Welcome Project",
      description:
        "Full app at 1440×900 — production SpaceSwitcher, CanvasLeftRail with desktop utilities, glass tokens, history panel, input bar, all five onboarding frames.",
      render: () => <WelcomeProjectApp />,
    },
  ],
};
